import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { CourseService } from '../../../pages/courses/course.service';
import { ToasterService } from '../../../providers/toaster/toaster.service';
import { environment } from '../../../../environments/environment';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-course-card',
  standalone: true,
  imports: [CommonModule,FormsModule,RouterModule],
  templateUrl: './course-card.component.html',
  styleUrl: './course-card.component.css'
})
export class CourseCardComponent implements OnChanges {
  @Input() data: any
  @Output() statusChanged = new EventEmitter<any>();
  isStatusUpdating: boolean = false;

  constructor(private courseService: CourseService, private toasterService: ToasterService) {}

  private readonly courseTypeMap: Record<number, string> = {
    1: 'video',
    2: 'flashcard',
    3: 'exam'
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']?.currentValue) {
      this.data = this.normalizeStatusData(changes['data'].currentValue);
    }
  }
  // changeStatus is invoked from the template dropdown when the user selects
  // one of: Draft, Publish or Unpublish. It coordinates the UI and service:
  // 1) validates the course id
  // 2) calls the appropriate CourseService method
  // 3) shows a toast for success/failure
  // 4) updates local `data.status` and emits `statusChanged` so parent components
  //    can react (e.g. refresh lists)
  changeStatus(status: 'draft' | 'publish' | 'unpublish') {
    if (!this.data?.id) {
      this.toasterService.setToaster('error', 'Error', 'Course ID not found');
      return;
    }

    // Prevent changing from published to draft
    const currentStatus = this.data.status;
    if (currentStatus === 'published' && status === 'draft') {
      this.toasterService.setToaster('error', 'Error', 'Published courses cannot be changed back to draft');
      return;
    }

    let statusCall;
    let statusLabel = '';

    // Prepare optional extra fields that some backends expect on status change
    const extra = {
      course_type: (this.data.course_type ?? this.data.course_type_id ?? undefined),
      course_name: (this.data.course_name ?? this.data.title ?? undefined),
      description: (this.data.description ?? undefined),
    };

    switch (status) {
      case 'draft':
        statusCall = this.courseService.setCourseAsDraft(this.data.id, extra);
        statusLabel = 'Draft';
        break;
      case 'publish':
        statusCall = this.courseService.setCourseAsPublished(this.data.id, extra);
        statusLabel = 'Published';
        break;
      default:
        this.toasterService.setToaster('error', 'Error', 'Unsupported status');
        return;
    }

    // Subscribe and handle server response. We expect the server to return
    // an envelope like { status: 'success' | 'fail', message: string }
    this.isStatusUpdating = true;
    statusCall
      .pipe(finalize(() => {
        this.isStatusUpdating = false;
      }))
      .subscribe({
      next: (res: any) => {
        try {
            if (res?.body?.status === 'success') {
            // successful update
            this.toasterService.setToaster('success', 'Status Updated', `Course status changed to ${statusLabel}`);
            // reflect only status change locally
            const updated = { ...this.data };
            updated.is_published = status === 'publish' ? 1 : 0;
            updated.status = Number(updated.is_published) === 1 ? 'published' : 'draft';
            this.data = this.normalizeStatusData(updated);
            // let parent components know so they can refresh lists or counters
            this.statusChanged.emit({ courseId: this.data.id, status, data: this.data });
          } else {
            // server returned a non-success response
            const msg = res?.body?.message || 'Failed to change status';
            this.toasterService.setToaster('error', 'Error', msg);
          }
        } catch (error) {
          this.toasterService.setToaster('error', 'Error', 'Failed to update status');
        }
      },
      error: (error) => {
        // network or server error
        this.toasterService.setToaster('error', 'Error', error?.error?.message || 'Failed to update course status');
      }
    });
  }

  onStatusMenuClick(status: 'draft' | 'publish', triggerEl: HTMLElement, event: Event) {
    event.preventDefault();
    event.stopPropagation();

    
    this.changeStatus(status);

    (event.target as HTMLElement | null)?.blur();
    triggerEl?.blur();
    (document.activeElement as HTMLElement | null)?.blur();
  }

  private normalizeStatusData(value: any) {
    const normalized = { ...(value || {}) };
    normalized.is_published = Number(normalized?.is_published ?? 0);

    const parsedType = Number(normalized?.course_type);
    if (!Number.isNaN(parsedType) && this.courseTypeMap[parsedType]) {
      normalized.course_type = this.courseTypeMap[parsedType];
    } else if (!normalized?.course_type && normalized?.course_type_id && this.courseTypeMap[Number(normalized.course_type_id)]) {
      normalized.course_type = this.courseTypeMap[Number(normalized.course_type_id)];
    }

    normalized.status = normalized.is_published === 1 ? 'published' : 'draft';
    return normalized;
  }

  getCourseTypeSlug(): string {
    const rawType = this.data?.course_type;
    if (typeof rawType === 'string' && rawType.trim()) {
      return rawType.replace(/\s+/g, '').toLowerCase();
    }

    const numericType = Number(rawType ?? this.data?.course_type_id);
    if (!Number.isNaN(numericType) && this.courseTypeMap[numericType]) {
      return this.courseTypeMap[numericType];
    }

    return 'video';
  }

  getCourseTypeLabel(): string {
    const slug = this.getCourseTypeSlug();
    if (slug === 'flashcard') return 'Flashcard';
    if (slug === 'exam') return 'Exam';
    return 'Video';
  }

  getThumbnailUrl(): string {
    const thumbnail = String(this.data?.thumbnail ?? '').trim();
    if (!thumbnail) return 'assets/dummy_thumbnail.png';
    if (/^https?:\/\//i.test(thumbnail)) return thumbnail;

    const apiBase = environment.apiUrl.replace(/api\/v1\/?$/, '');
    const normalizedPath = thumbnail.startsWith('/') ? thumbnail : `/${thumbnail}`;
    return `${apiBase}${normalizedPath}`;
  }
}
